import { createEnv } from '@t3-oss/env-core'

import { z } from 'zod'

// Helper function to convert empty strings to undefined for optional env vars
const emptyStringToUndefined = (val: unknown) => (val === '' ? undefined : val)

const stringToBoolean = (val: string) => val.toLowerCase() === 'true'

// VITE_BUILD_AGENT_URL is EITHER an absolute http(s) URL (EVA hosted on a
// dedicated origin) OR a root-relative path such as `/api-agent` (EVA proxied
// under the app's own origin — the common on-prem / customer-DNS shape, where a
// baked absolute URL breaks the moment the app is reached on a different
// host/port). `z.string().url()` rejects the root-relative form, which blocked
// EVA on customer DNS; validate the union explicitly instead. The resolver
// (agentApi.resolveAgentApiUrl) returns the configured value (trailing
// separators trimmed) and concatenates request paths onto it, so `/api-agent`
// + `/sessions` resolves
// against the current origin — no absolute host required. Exported so the
// contract (accept root-relative, reject garbage) is locked by a unit test.
export const buildAgentUrlSchema = z.string().refine(
  (val) => {
    // A base URL must carry NO query or fragment: the resolver concatenates the
    // request path directly onto it, so a `?`/`#` component would swallow that
    // path (`/api-agent?x` + `/sessions` -> `/api-agent?x/sessions`, which the
    // browser sends as a query, not a path). Reject up front for both shapes.
    if (val.includes('?') || val.includes('#')) return false
    // Absolute http(s) URL: must PARSE and keep a real origin. `new URL` rejects
    // malformed authorities such as `http://?` (throws), and a value whose
    // parsed origin is the literal "null" (opaque) is not a usable agent base.
    if (/^https?:\/\//i.test(val)) {
      try {
        const u = new URL(val)
        return (
          (u.protocol === 'http:' || u.protocol === 'https:') &&
          u.origin !== 'null'
        )
      } catch {
        return false
      }
    }
    // Root-relative path: resolve against a sentinel origin and require the
    // origin to SURVIVE. `/api-agent` keeps the sentinel origin (accepted);
    // the network-path reference `//evil.example.com` rewrites the origin to
    // evil.example.com (rejected) — prefix-matching `startsWith('/')` used to
    // let it through and the verbatim resolver would then target another host.
    if (val.startsWith('/')) {
      // The bare root `/` passes the origin check, but concatenating `/sessions`
      // onto it yields the protocol-relative `//sessions` (a network-path
      // reference to host `sessions`). Require ≥1 path segment after the slash.
      if (val === '/') return false
      const sentinel = 'https://linq-build-agent.sentinel.invalid'
      try {
        return new URL(val, sentinel).origin === sentinel
      } catch {
        return false
      }
    }
    return false
  },
  {
    message:
      'VITE_BUILD_AGENT_URL must be an absolute http(s) URL (e.g. https://eva.example.com) or a same-origin root-relative path beginning with a single "/" and at least one path segment (e.g. /api-agent); the bare root "/", protocol-relative (//host), query/fragment components, and malformed URLs are rejected',
  },
)

export const env = createEnv({
  /*
   * Client-side environment variables schema
   */
  clientPrefix: 'VITE_',
  client: {
    VITE_NATS_WS_URL: z.preprocess(
      emptyStringToUndefined,
      z
        .string()
        .url()
        .default(
          `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/linq/streams/`,
        ),
    ),
    VITE_NATS_USER: z.string(),
    VITE_NATS_PASSWORD: z.string(),
    VITE_ENABLE_MSW: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_LINQ_SERVICE_FRAMEWORK_PUBLISH_FORMAT: z.preprocess(
      emptyStringToUndefined,
      z.enum(['protobuf', 'json']).default('json'),
    ),

    VITE_FARO_COLLECTOR_URL: z.preprocess(
      emptyStringToUndefined,
      z.string().url().default(`${window.location.origin}/collect`),
    ),
    VITE_FARO_APP_NAME: z.preprocess(
      emptyStringToUndefined,
      z.string().default('linq-frontend'),
    ),
    VITE_FARO_APP_ENVIRONMENT: z.preprocess(
      emptyStringToUndefined,
      z.string().default('production'),
    ),
    VITE_FARO_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_AUTH_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_AUTH_CLIENT_ID: z.preprocess(
      emptyStringToUndefined,
      z.string().optional(),
    ),
    VITE_AUTH_AUTHORITY: z.preprocess(
      emptyStringToUndefined,
      z
        .string()
        .url()
        .default(
          window.location.origin + '/linq/auth/application/o/linq-frontend/',
        ),
    ),
    VITE_GRAPHQL_URL: z.preprocess(
      emptyStringToUndefined,
      z
        .string()
        .url()
        .default(window.location.origin + '/graphql'),
    ),
    VITE_BFF_URL: z.preprocess(
      emptyStringToUndefined,
      z.string().url().default(window.location.origin),
    ),
    VITE_DISABLE_HTTPS: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_TANSTACK_DEVTOOLS_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    // EVA MCP agent: when true, the build-agent UI (sparkles button,
    // BuildAgentPanel) is rendered. Default off. Paired with LDP's
    // EVA_MCP_ENABLED + lab-skills helm deployment.
    VITE_EVA_AGENT_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    // Multi-plan simulation: when true, the simulate button offers an
    // "OR-tools multi-plan" option that sends `multiPlan: true` on
    // canvas.simulateWorkflowExecutionPreset (gateway v3.51+). Requires
    // LDP OR_TOOLS_CLUSTER_ENABLED=true + gateway
    // BFF_PLANNER_ORCHESTRATOR_ENABLED=true.
    VITE_MULTI_PLAN_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    // Notebooks PoC: when true, a flag-gated "Analyse in notebook" button is
    // shown on a finished run's detail panel, opening a Jupyter session
    // embedded in the UI. Default off. Local PoC only.
    VITE_NOTEBOOK_ANALYSIS_ENABLED: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_ENABLE_MOCK_DEVICE_CONNECTION: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_MOCK_INSTRUMENTS: z.preprocess(
      emptyStringToUndefined,
      z.enum(['true', 'false']).default('false').transform(stringToBoolean),
    ),
    VITE_BUILD_AGENT_URL: z.preprocess(
      emptyStringToUndefined,
      buildAgentUrlSchema.optional(),
    ),
    VITE_DEV_PERMISSIONS: z.preprocess(
      emptyStringToUndefined,
      z.string().optional(),
    ),
  },
  server: {},
  runtimeEnv: import.meta.env, // eslint-disable-line no-restricted-syntax
})
